/**
 * UDP Onboarding Field Configuration
 * ====================================
 * Single source of truth for all per-field onboarding metadata.
 * Both frontend and backend import this.
 *
 * IMPORTANT: All string properties (title, subtitle, successMessage,
 * step group label) are i18n KEYS, not display text. The frontend resolves
 * them via useTranslations("udp") → t(config.title).
 *
 * Translation files:
 *   - messages/en/udp.json (English — under "udp" root key)
 *   - messages/hi/udp.json (Hindi — under "udp" root key)
 *
 * @module udp-schema/onboarding-config
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface UdpOnboardingFieldConfig {
  /** i18n key for the question heading (resolved via t(title)) */
  title: string;
  /** i18n key for help text below the heading */
  subtitle?: string;
  /** i18n key for toast message on successful save */
  successMessage: string;
  /** Whether the field is required to proceed */
  required: boolean;
  /** i18n namespace for option labels: t(`options.${optionNs}.${value}`) */
  optionNs?: string;
  /** Whether option descriptions exist (uses `options.${optionNs}Desc.*` keys) */
  hasDesc?: boolean;
}

/** Step grouping for the UDP onboarding flow */
export interface UdpOnboardingStepGroup {
  /** i18n key for section label */
  label: string;
  /** Emoji character (e.g., '📝', '🏛️') rendered directly */
  icon: string;
  /** Ordered list of field keys in this section */
  fields: string[];
}

// ─── Per-field onboarding metadata ──────────────────────────────────────────

export const UDP_ONBOARDING_FIELDS: Record<string, UdpOnboardingFieldConfig> = {
  // ── Step 1: Profile ──
  fullName: {
    title: 'fullName.title',
    subtitle: 'fullName.subtitle',
    successMessage: 'fullName.success',
    required: false,
  },
  displayName: {
    title: 'displayName.title',
    subtitle: 'displayName.subtitle',
    successMessage: 'displayName.success',
    required: false,
  },
  gender: {
    title: 'gender.title',
    subtitle: 'gender.subtitle',
    successMessage: 'gender.success',
    required: false,
    optionNs: 'gender',
    hasDesc: true,
  },
  dateOfBirth: {
    title: 'dateOfBirth.title',
    subtitle: 'dateOfBirth.subtitle',
    successMessage: 'dateOfBirth.success',
    required: false,
  },
  whatsappNumber: {
    title: 'whatsappNumber.title',
    subtitle: 'whatsappNumber.subtitle',
    successMessage: 'whatsappNumber.success',
    required: false,
  },
  primaryLanguage: {
    title: 'primaryLanguage.title',
    subtitle: 'primaryLanguage.subtitle',
    successMessage: 'primaryLanguage.success',
    required: false,
    optionNs: 'language',
  },
  secondaryLanguages: {
    title: 'secondaryLanguages.title',
    subtitle: 'secondaryLanguages.subtitle',
    successMessage: 'secondaryLanguages.success',
    required: false,
    optionNs: 'language',
  },
  familyReligion: {
    title: 'familyReligion.title',
    subtitle: 'familyReligion.subtitle',
    successMessage: 'familyReligion.success',
    required: false,
    optionNs: 'religion',
    hasDesc: true,
  },
  personalReligion: {
    title: 'personalReligion.title',
    subtitle: 'personalReligion.subtitle',
    successMessage: 'personalReligion.success',
    required: false,
    optionNs: 'religion',
    hasDesc: true,
  },
  reservationStatus: {
    title: 'reservationStatus.title',
    subtitle: 'reservationStatus.subtitle',
    successMessage: 'reservationStatus.success',
    required: false,
    optionNs: 'reservation',
    hasDesc: true,
  },
  minorityStatus: {
    title: 'minorityStatus.title',
    subtitle: 'minorityStatus.subtitle',
    successMessage: 'minorityStatus.success',
    required: false,
    optionNs: 'minority',
    hasDesc: true,
  },

  // ── Step 2: Political Association & History ──
  politicalSpectrum: {
    title: 'politicalSpectrum.title',
    subtitle: 'politicalSpectrum.subtitle',
    successMessage: 'politicalSpectrum.success',
    required: false,
    optionNs: 'politicalSpectrum',
  },
  selfPartyAssociation: {
    title: 'selfPartyAssociation.title',
    subtitle: 'selfPartyAssociation.subtitle',
    successMessage: 'selfPartyAssociation.success',
    required: false,
    optionNs: 'party',
  },
  partyAssociationRole: {
    title: 'partyAssociationRole.title',
    subtitle: 'partyAssociationRole.subtitle',
    successMessage: 'partyAssociationRole.success',
    required: false,
    optionNs: 'partyRole',
    hasDesc: true,
  },
  familyPartyAssociation: {
    title: 'familyPartyAssociation.title',
    subtitle: 'familyPartyAssociation.subtitle',
    successMessage: 'familyPartyAssociation.success',
    required: false,
    optionNs: 'party',
  },
  relativesInPolitics: {
    title: 'relativesInPolitics.title',
    subtitle: 'relativesInPolitics.subtitle',
    successMessage: 'relativesInPolitics.success',
    required: false,
    optionNs: 'relativesInPolitics',
    hasDesc: true,
  },
  // Composite sub-steps. The yes/no question is the screen title; if the
  // user answers "yes", the level + year inputs appear inline below on the
  // same screen (not as separate sub-steps). Level / year fields still
  // exist in the data model + Zod schema (and retain their `conditionalOn`
  // metadata so save logic can validate them) — they just aren't navigated
  // to as standalone screens.
  hasContestedElection: {
    title: 'hasContestedElection.title',
    subtitle: 'hasContestedElection.subtitle',
    successMessage: 'hasContestedElection.success',
    required: false,
    optionNs: 'yesNoPrefer',
  },
  hasHeldPublicOffice: {
    title: 'hasHeldPublicOffice.title',
    subtitle: 'hasHeldPublicOffice.subtitle',
    successMessage: 'hasHeldPublicOffice.success',
    required: false,
    optionNs: 'yesNoPrefer',
  },
  networkAndCivicEngagement: {
    title: 'networkAndCivicEngagement.title',
    subtitle: 'networkAndCivicEngagement.subtitle',
    successMessage: 'networkAndCivicEngagement.success',
    required: false,
    optionNs: 'civic',
  },

  // ── Step 3: Electoral Behaviour ──
  epicNumber: {
    title: 'epicNumber.title',
    subtitle: 'epicNumber.subtitle',
    successMessage: 'epicNumber.success',
    required: false,
  },
  willVoteThisTime: {
    title: 'willVoteThisTime.title',
    subtitle: 'willVoteThisTime.subtitle',
    successMessage: 'willVoteThisTime.success',
    required: false,
    optionNs: 'willVote',
  },
  didVoteLastTime: {
    title: 'didVoteLastTime.title',
    subtitle: 'didVoteLastTime.subtitle',
    successMessage: 'didVoteLastTime.success',
    required: false,
    optionNs: 'yesNoPrefer',
  },
  votingFrequency: {
    title: 'votingFrequency.title',
    subtitle: 'votingFrequency.subtitle',
    successMessage: 'votingFrequency.success',
    required: false,
    optionNs: 'votingFreq',
  },
  primaryVotingIssues: {
    title: 'primaryVotingIssues.title',
    subtitle: 'primaryVotingIssues.subtitle',
    successMessage: 'primaryVotingIssues.success',
    required: false,
    optionNs: 'votingIssue',
  },

  // ── Step 4: Leadership & Ambitions ──
  participateInRevolution: {
    title: 'participateInRevolution.title',
    subtitle: 'participateInRevolution.subtitle',
    successMessage: 'participateInRevolution.success',
    required: false,
    optionNs: 'participate',
  },
  desiredPoliticalPosition: {
    title: 'desiredPoliticalPosition.title',
    subtitle: 'desiredPoliticalPosition.subtitle',
    successMessage: 'desiredPoliticalPosition.success',
    required: false,
    optionNs: 'position',
  },
  leadershipSkillset: {
    title: 'leadershipSkillset.title',
    subtitle: 'leadershipSkillset.subtitle',
    successMessage: 'leadershipSkillset.success',
    required: false,
    optionNs: 'leadershipSkill',
  },
  politicalExposure: {
    title: 'politicalExposure.title',
    subtitle: 'politicalExposure.subtitle',
    successMessage: 'politicalExposure.success',
    required: false,
    optionNs: 'exposure',
  },
  comfortWithVisibility: {
    title: 'comfortWithVisibility.title',
    subtitle: 'comfortWithVisibility.subtitle',
    successMessage: 'comfortWithVisibility.success',
    required: false,
    optionNs: 'visibility',
  },
  socialInfluenceLevel: {
    title: 'socialInfluenceLevel.title',
    subtitle: 'socialInfluenceLevel.subtitle',
    successMessage: 'socialInfluenceLevel.success',
    required: false,
    optionNs: 'influence',
  },
  pastExperience: {
    title: 'pastExperience.title',
    subtitle: 'pastExperience.subtitle',
    successMessage: 'pastExperience.success',
    required: false,
    optionNs: 'experience',
  },

  // ── Step 5: Psychological Assessment ──
  mbtiType: {
    title: 'mbtiType.title',
    subtitle: 'mbtiType.subtitle',
    successMessage: 'mbtiType.success',
    required: false,
  },
  oceanScores: {
    title: 'oceanScores.title',
    subtitle: 'oceanScores.subtitle',
    successMessage: 'oceanScores.success',
    required: false,
  },

  // ── Step 6: Education ──
  highestQualification: {
    title: 'highestQualification.title',
    subtitle: 'highestQualification.subtitle',
    successMessage: 'highestQualification.success',
    required: false,
  },
  education10th: {
    title: 'education10th.title',
    subtitle: 'education10th.subtitle',
    successMessage: 'education10th.success',
    required: false,
  },
  education12th: {
    title: 'education12th.title',
    subtitle: 'education12th.subtitle',
    successMessage: 'education12th.success',
    required: false,
  },
  diplomas: {
    title: 'diplomas.title',
    subtitle: 'diplomas.subtitle',
    successMessage: 'diplomas.success',
    required: false,
  },
  graduations: {
    title: 'graduations.title',
    subtitle: 'graduations.subtitle',
    successMessage: 'graduations.success',
    required: false,
  },
  postGraduations: {
    title: 'postGraduations.title',
    subtitle: 'postGraduations.subtitle',
    successMessage: 'postGraduations.success',
    required: false,
  },
  doctorates: {
    title: 'doctorates.title',
    subtitle: 'doctorates.subtitle',
    successMessage: 'doctorates.success',
    required: false,
  },
  postDoctorates: {
    title: 'postDoctorates.title',
    subtitle: 'postDoctorates.subtitle',
    successMessage: 'postDoctorates.success',
    required: false,
  },
  otherCertificates: {
    title: 'otherCertificates.title',
    subtitle: 'otherCertificates.subtitle',
    successMessage: 'otherCertificates.success',
    required: false,
  },

  // ── Step 7: Employment & Occupation ──
  employmentStatus: {
    title: 'employmentStatus.title',
    subtitle: 'employmentStatus.subtitle',
    successMessage: 'employmentStatus.success',
    required: false,
    optionNs: 'employment',
  },
  occupation: {
    title: 'occupation.title',
    subtitle: 'occupation.subtitle',
    successMessage: 'occupation.success',
    required: false,
    optionNs: 'occupation',
    hasDesc: true,
  },
  workShift: {
    title: 'workShift.title',
    subtitle: 'workShift.subtitle',
    successMessage: 'workShift.success',
    required: false,
    optionNs: 'shift',
  },
  comfortableWithShift: {
    title: 'comfortableWithShift.title',
    subtitle: 'comfortableWithShift.subtitle',
    successMessage: 'comfortableWithShift.success',
    required: false,
    optionNs: 'comfort',
  },
  desiredShift: {
    title: 'desiredShift.title',
    subtitle: 'desiredShift.subtitle',
    successMessage: 'desiredShift.success',
    required: false,
    optionNs: 'shift',
  },
  incomeRange: {
    title: 'incomeRange.title',
    subtitle: 'incomeRange.subtitle',
    successMessage: 'incomeRange.success',
    required: false,
    optionNs: 'income',
  },
  dreamOccupation: {
    title: 'dreamOccupation.title',
    subtitle: 'dreamOccupation.subtitle',
    successMessage: 'dreamOccupation.success',
    required: false,
    optionNs: 'occupation',
    hasDesc: true,
  },

  // ── Step 8: Mental Health & Neurodiversity ──
  neurodiverseIdentity: {
    title: 'neurodiverseIdentity.title',
    subtitle: 'neurodiverseIdentity.subtitle',
    successMessage: 'neurodiverseIdentity.success',
    required: false,
    optionNs: 'neurodiverseIdentity',
    hasDesc: true,
  },
  neurodevelopmentalConditions: {
    title: 'neurodevelopmentalConditions.title',
    subtitle: 'neurodevelopmentalConditions.subtitle',
    successMessage: 'neurodevelopmentalConditions.success',
    required: false,
    optionNs: 'neurodevelopmental',
    hasDesc: true,
  },
  mentalHealthConditions: {
    title: 'mentalHealthConditions.title',
    subtitle: 'mentalHealthConditions.subtitle',
    successMessage: 'mentalHealthConditions.success',
    required: false,
    optionNs: 'mentalHealth',
    hasDesc: true,
  },
  personalityPatterns: {
    title: 'personalityPatterns.title',
    subtitle: 'personalityPatterns.subtitle',
    successMessage: 'personalityPatterns.success',
    required: false,
    optionNs: 'personalityPattern',
    hasDesc: true,
  },
  neurologicalChronicConditions: {
    title: 'neurologicalChronicConditions.title',
    subtitle: 'neurologicalChronicConditions.subtitle',
    successMessage: 'neurologicalChronicConditions.success',
    required: false,
    optionNs: 'neurologicalChronic',
    hasDesc: true,
  },
  ageOfRealization: {
    title: 'ageOfRealization.title',
    subtitle: 'ageOfRealization.subtitle',
    successMessage: 'ageOfRealization.success',
    required: false,
    optionNs: 'ageOfRealization',
  },
  sensoryProfile: {
    title: 'sensoryProfile.title',
    subtitle: 'sensoryProfile.subtitle',
    successMessage: 'sensoryProfile.success',
    required: false,
    optionNs: 'sensoryProfile',
  },
  cognitiveStrengths: {
    title: 'cognitiveStrengths.title',
    subtitle: 'cognitiveStrengths.subtitle',
    successMessage: 'cognitiveStrengths.success',
    required: false,
    optionNs: 'cognitiveStrength',
  },
  executiveFunctionChallenges: {
    title: 'executiveFunctionChallenges.title',
    subtitle: 'executiveFunctionChallenges.subtitle',
    successMessage: 'executiveFunctionChallenges.success',
    required: false,
    optionNs: 'executiveFunctionChallenge',
  },
  learningStyle: {
    title: 'learningStyle.title',
    subtitle: 'learningStyle.subtitle',
    successMessage: 'learningStyle.success',
    required: false,
    optionNs: 'learningStyle',
  },
  communicationPreference: {
    title: 'communicationPreference.title',
    subtitle: 'communicationPreference.subtitle',
    successMessage: 'communicationPreference.success',
    required: false,
    optionNs: 'communicationPreference',
  },
  supportAndAccommodations: {
    title: 'supportAndAccommodations.title',
    subtitle: 'supportAndAccommodations.subtitle',
    successMessage: 'supportAndAccommodations.success',
    required: false,
    optionNs: 'supportAccommodation',
  },
  maskingAndBurnout: {
    title: 'maskingAndBurnout.title',
    subtitle: 'maskingAndBurnout.subtitle',
    successMessage: 'maskingAndBurnout.success',
    required: false,
    optionNs: 'maskingBurnout',
    hasDesc: true,
  },
  impactOnLife: {
    title: 'impactOnLife.title',
    subtitle: 'impactOnLife.subtitle',
    successMessage: 'impactOnLife.success',
    required: false,
    optionNs: 'impactOnLife',
    hasDesc: true,
  },

  // ── Step 9: Health & Lifestyle ──
  disabilityStatus: {
    title: 'disabilityStatus.title',
    subtitle: 'disabilityStatus.subtitle',
    successMessage: 'disabilityStatus.success',
    required: false,
    optionNs: 'disability',
  },
  generalHealth: {
    title: 'generalHealth.title',
    subtitle: 'generalHealth.subtitle',
    successMessage: 'generalHealth.success',
    required: false,
    optionNs: 'health',
  },
  healthInsurance: {
    title: 'healthInsurance.title',
    subtitle: 'healthInsurance.subtitle',
    successMessage: 'healthInsurance.success',
    required: false,
    optionNs: 'insurance',
  },
  diet: {
    title: 'diet.title',
    subtitle: 'diet.subtitle',
    successMessage: 'diet.success',
    required: false,
    optionNs: 'diet',
    hasDesc: true,
  },
  physicalActivity: {
    title: 'physicalActivity.title',
    subtitle: 'physicalActivity.subtitle',
    successMessage: 'physicalActivity.success',
    required: false,
    optionNs: 'activity',
  },
  sleepQuality: {
    title: 'sleepQuality.title',
    subtitle: 'sleepQuality.subtitle',
    successMessage: 'sleepQuality.success',
    required: false,
    optionNs: 'sleep',
  },
  substanceUse: {
    title: 'substanceUse.title',
    subtitle: 'substanceUse.subtitle',
    successMessage: 'substanceUse.success',
    required: false,
    optionNs: 'substance',
  },
  stressLevel: {
    title: 'stressLevel.title',
    subtitle: 'stressLevel.subtitle',
    successMessage: 'stressLevel.success',
    required: false,
    optionNs: 'stress',
  },

  // ── Step 10: World View & Experience ──
  viewOnAnimalCruelty: {
    title: 'viewOnAnimalCruelty.title',
    subtitle: 'viewOnAnimalCruelty.subtitle',
    successMessage: 'viewOnAnimalCruelty.success',
    required: false,
    optionNs: 'animalCruelty',
  },
  viewOnLgbtqStatements: {
    title: 'viewOnLgbtqStatements.title',
    subtitle: 'viewOnLgbtqStatements.subtitle',
    successMessage: 'viewOnLgbtqStatements.success',
    required: false,
    optionNs: 'lgbtq',
  },
  viewOnTransgenderInclusion: {
    title: 'viewOnTransgenderInclusion.title',
    subtitle: 'viewOnTransgenderInclusion.subtitle',
    successMessage: 'viewOnTransgenderInclusion.success',
    required: false,
    optionNs: 'transgender',
  },
  facingCasteDiscrimination: {
    title: 'facingCasteDiscrimination.title',
    subtitle: 'facingCasteDiscrimination.subtitle',
    successMessage: 'facingCasteDiscrimination.success',
    required: false,
    optionNs: 'discrimination',
  },
  casteDiscriminationPlaces: {
    title: 'casteDiscriminationPlaces.title',
    subtitle: 'casteDiscriminationPlaces.subtitle',
    successMessage: 'casteDiscriminationPlaces.success',
    required: false,
    optionNs: 'discriminationPlace',
  },
  viewOnGenderEquality: {
    title: 'viewOnGenderEquality.title',
    subtitle: 'viewOnGenderEquality.subtitle',
    successMessage: 'viewOnGenderEquality.success',
    required: false,
    optionNs: 'genderEquality',
  },

  // ── Step 11: Residence & Address ──
  currentAddress: {
    title: 'currentAddress.title',
    subtitle: 'currentAddress.subtitle',
    successMessage: 'currentAddress.success',
    required: false,
  },
  permanentAddress: {
    title: 'permanentAddress.title',
    subtitle: 'permanentAddress.subtitle',
    successMessage: 'permanentAddress.success',
    required: false,
  },
  homeNativePlace: {
    title: 'homeNativePlace.title',
    subtitle: 'homeNativePlace.subtitle',
    successMessage: 'homeNativePlace.success',
    required: false,
  },
  ancestralPlace: {
    title: 'ancestralPlace.title',
    subtitle: 'ancestralPlace.subtitle',
    successMessage: 'ancestralPlace.success',
    required: false,
  },

  // ── Step 12: Household & Economic Profile ──
  maritalStatus: {
    title: 'maritalStatus.title',
    subtitle: 'maritalStatus.subtitle',
    successMessage: 'maritalStatus.success',
    required: false,
    optionNs: 'marital',
  },
  familyMembers: {
    title: 'familyMembers.title',
    subtitle: 'familyMembers.subtitle',
    successMessage: 'familyMembers.success',
    required: false,
    optionNs: 'family',
  },
  // ── Family-link sub-steps (one per relation type) ─────────────────────────
  // Each is conditionally shown when the user has selected the matching value
  // in `familyMembers` (sub-step 2). Visibility is enforced by the
  // `conditionalOn: { field: 'familyMembers', includesAny: [...] }` rule on
  // each profile-fields entry. Sub-step body renders the search UI + already-
  // linked list.
  linkedMother: {
    title: 'linkedMother.title',
    subtitle: 'linkedMother.subtitle',
    successMessage: 'linkedMother.success',
    required: false,
  },
  linkedStepMother: {
    title: 'linkedStepMother.title',
    subtitle: 'linkedStepMother.subtitle',
    successMessage: 'linkedStepMother.success',
    required: false,
  },
  linkedFather: {
    title: 'linkedFather.title',
    subtitle: 'linkedFather.subtitle',
    successMessage: 'linkedFather.success',
    required: false,
  },
  linkedStepFather: {
    title: 'linkedStepFather.title',
    subtitle: 'linkedStepFather.subtitle',
    successMessage: 'linkedStepFather.success',
    required: false,
  },
  linkedSpouses: {
    title: 'linkedSpouses.title',
    subtitle: 'linkedSpouses.subtitle',
    successMessage: 'linkedSpouses.success',
    required: false,
  },
  linkedBloodSisters: {
    title: 'linkedBloodSisters.title',
    subtitle: 'linkedBloodSisters.subtitle',
    successMessage: 'linkedBloodSisters.success',
    required: false,
  },
  linkedHalfSisters: {
    title: 'linkedHalfSisters.title',
    subtitle: 'linkedHalfSisters.subtitle',
    successMessage: 'linkedHalfSisters.success',
    required: false,
  },
  linkedStepSisters: {
    title: 'linkedStepSisters.title',
    subtitle: 'linkedStepSisters.subtitle',
    successMessage: 'linkedStepSisters.success',
    required: false,
  },
  linkedBloodBrothers: {
    title: 'linkedBloodBrothers.title',
    subtitle: 'linkedBloodBrothers.subtitle',
    successMessage: 'linkedBloodBrothers.success',
    required: false,
  },
  linkedHalfBrothers: {
    title: 'linkedHalfBrothers.title',
    subtitle: 'linkedHalfBrothers.subtitle',
    successMessage: 'linkedHalfBrothers.success',
    required: false,
  },
  linkedStepBrothers: {
    title: 'linkedStepBrothers.title',
    subtitle: 'linkedStepBrothers.subtitle',
    successMessage: 'linkedStepBrothers.success',
    required: false,
  },
  linkedDaughters: {
    title: 'linkedDaughters.title',
    subtitle: 'linkedDaughters.subtitle',
    successMessage: 'linkedDaughters.success',
    required: false,
  },
  linkedSons: {
    title: 'linkedSons.title',
    subtitle: 'linkedSons.subtitle',
    successMessage: 'linkedSons.success',
    required: false,
  },
  linkedOthers: {
    title: 'linkedOthers.title',
    subtitle: 'linkedOthers.subtitle',
    successMessage: 'linkedOthers.success',
    required: false,
  },
  numberOfDependents: {
    title: 'numberOfDependents.title',
    subtitle: 'numberOfDependents.subtitle',
    successMessage: 'numberOfDependents.success',
    required: false,
  },
  housingType: {
    title: 'housingType.title',
    subtitle: 'housingType.subtitle',
    successMessage: 'housingType.success',
    required: false,
    optionNs: 'housing',
  },
  householdIncome: {
    title: 'householdIncome.title',
    subtitle: 'householdIncome.subtitle',
    successMessage: 'householdIncome.success',
    required: false,
    optionNs: 'hhIncome',
  },
  bplCard: {
    title: 'bplCard.title',
    subtitle: 'bplCard.subtitle',
    successMessage: 'bplCard.success',
    required: false,
    optionNs: 'bpl',
  },
  digitalAccess: {
    title: 'digitalAccess.title',
    subtitle: 'digitalAccess.subtitle',
    successMessage: 'digitalAccess.success',
    required: false,
    optionNs: 'digital',
  },
  migrationStatus: {
    title: 'migrationStatus.title',
    subtitle: 'migrationStatus.subtitle',
    successMessage: 'migrationStatus.success',
    required: false,
    optionNs: 'migration',
  },
  landOwnership: {
    title: 'landOwnership.title',
    subtitle: 'landOwnership.subtitle',
    successMessage: 'landOwnership.success',
    optionNs: 'land',
    required: false,
  },
  primaryIncomeSource: {
    title: 'primaryIncomeSource.title',
    subtitle: 'primaryIncomeSource.subtitle',
    successMessage: 'primaryIncomeSource.success',
    optionNs: 'incomeSource',
    required: false,
  },
};

// ─── Step grouping ──────────────────────────────────────────────────────────

export const UDP_ONBOARDING_STEP_GROUPS: UdpOnboardingStepGroup[] = [
  {
    label: 'steps.profile',
    icon: '📝',
    fields: [
      'fullName',
      'displayName',
      'gender',
      'dateOfBirth',
      'whatsappNumber',
      'primaryLanguage',
      'secondaryLanguages',
      'familyReligion',
      'personalReligion',
      'reservationStatus',
      'minorityStatus',
    ],
  },
  {
    label: 'steps.politicalAssociation',
    icon: '🏛️',
    fields: [
      'politicalSpectrum',
      'selfPartyAssociation',
      'partyAssociationRole',
      'familyPartyAssociation',
      'relativesInPolitics',
      // Composite sub-steps — see configFields above. Level/year are
      // rendered inline within these screens, not as separate sub-steps.
      'hasContestedElection',
      'hasHeldPublicOffice',
      'networkAndCivicEngagement',
    ],
  },
  {
    label: 'steps.electoralBehaviour',
    icon: '🗳️',
    fields: [
      'epicNumber',
      'willVoteThisTime',
      'didVoteLastTime',
      'votingFrequency',
      'primaryVotingIssues',
    ],
  },
  {
    label: 'steps.leadershipAmbitions',
    icon: '👑',
    fields: [
      'participateInRevolution',
      'leadershipSkillset',
      'politicalExposure',
      'comfortWithVisibility',
      'socialInfluenceLevel',
      'pastExperience',
      'desiredPoliticalPosition',
    ],
  },
  {
    label: 'steps.psychologicalAssessment',
    icon: '🧩',
    fields: ['mbtiType', 'oceanScores'],
  },
  {
    label: 'steps.education',
    icon: '🎓',
    fields: [
      'highestQualification',
      'education10th',
      'education12th',
      'diplomas',
      'graduations',
      'postGraduations',
      'doctorates',
      'postDoctorates',
      'otherCertificates',
    ],
  },
  {
    label: 'steps.employment',
    icon: '💼',
    fields: [
      'employmentStatus',
      'occupation',
      'workShift',
      'comfortableWithShift',
      'desiredShift',
      'incomeRange',
      'dreamOccupation',
    ],
  },
  {
    label: 'steps.mentalHealthNeurodiversity',
    icon: '🧠',
    fields: [
      'neurodiverseIdentity',
      'neurodevelopmentalConditions',
      'mentalHealthConditions',
      'personalityPatterns',
      'neurologicalChronicConditions',
      'ageOfRealization',
      'sensoryProfile',
      'cognitiveStrengths',
      'executiveFunctionChallenges',
      'learningStyle',
      'communicationPreference',
      'supportAndAccommodations',
      'maskingAndBurnout',
      'impactOnLife',
    ],
  },
  {
    label: 'steps.healthLifestyle',
    icon: '🏥',
    fields: [
      'disabilityStatus',
      'generalHealth',
      'healthInsurance',
      'diet',
      'physicalActivity',
      'sleepQuality',
      'substanceUse',
      'stressLevel',
    ],
  },
  {
    label: 'steps.worldView',
    icon: '🌿',
    fields: [
      'viewOnAnimalCruelty',
      'viewOnLgbtqStatements',
      'viewOnTransgenderInclusion',
      'facingCasteDiscrimination',
      'casteDiscriminationPlaces',
      'viewOnGenderEquality',
    ],
  },
  {
    label: 'steps.residence',
    icon: '🏠',
    fields: ['currentAddress', 'permanentAddress', 'homeNativePlace', 'ancestralPlace'],
  },
  {
    label: 'steps.household',
    icon: '🏡',
    fields: [
      'maritalStatus',
      'familyMembers',
      // 14 conditional family-link sub-steps (female-first ordering).
      // Each appears only when the matching value is in `familyMembers`.
      'linkedMother',
      'linkedStepMother',
      'linkedFather',
      'linkedStepFather',
      'linkedSpouses',
      'linkedBloodSisters',
      'linkedHalfSisters',
      'linkedStepSisters',
      'linkedBloodBrothers',
      'linkedHalfBrothers',
      'linkedStepBrothers',
      'linkedDaughters',
      'linkedSons',
      'linkedOthers',
      'numberOfDependents',
      'housingType',
      'householdIncome',
      'bplCard',
      'digitalAccess',
      'migrationStatus',
      'landOwnership',
      'primaryIncomeSource',
    ],
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Get onboarding config for a single field. */
export function getUdpOnboardingFieldConfig(
  fieldKey: string | undefined,
): UdpOnboardingFieldConfig | undefined {
  if (!fieldKey) return undefined;
  return UDP_ONBOARDING_FIELDS[fieldKey];
}

/** Total number of main steps. */
export const UDP_TOTAL_MAIN_STEPS = UDP_ONBOARDING_STEP_GROUPS.length;

/** Total number of sub-steps across all main steps. */
export const UDP_TOTAL_SUB_STEPS = UDP_ONBOARDING_STEP_GROUPS.reduce(
  (sum, g) => sum + g.fields.length,
  0,
);
